import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UserCircle,
  TreeDeciduous,
  UserPlus,
  Mic,
  Mail,
  ChevronDown,
  Check,
  Loader2,
  ChevronUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useOnboarding } from './OnboardingContext';
import { humanizeError } from '../../utils/errorHandling';
import { getApiBase } from '../../utils/apiBase';
import { buildTreePeopleAddPersonUrl } from '../../utils/archiveNavigation';
import { treeHref } from '../../utils/prettyArchiveLinks';

const API = getApiBase();

const STEPS = [
  { id: 1, key: 'account', title: 'Create your account', icon: UserCircle },
  { id: 2, key: 'tree', title: 'Start your first family archive', icon: TreeDeciduous },
  { id: 3, key: 'person', title: 'Add your first person', icon: UserPlus },
  { id: 4, key: 'memory', title: 'Record your first story', icon: Mic },
  { id: 5, key: 'invite', title: 'Invite family (optional)', icon: Mail },
];

/**
 * MVP onboarding checklist: tree-first copy, highlights next incomplete step,
 * collapses completed rows. Step 5 does not block completion of the checklist shell.
 */
export function OnboardingChecklist({
  user,
  trees = [],
  onOpenCreateTree,
  className = '',
  /** `tree`: minimize uses tree rail state. `dashboard`: minimize/dismiss use dashboard keys in OnboardingContext. */
  surface = 'tree',
  /** Tree page: collapse into “Your setup guide” strip (uses `treeChecklistCollapsed`). Ignored when `surface` is `dashboard`. */
  showMinimizeControl = true,
}) {
  const {
    hasCreatedFirstTree,
    hasAddedFirstPerson,
    hasRecordedFirstStory,
    hasInviteSent,
    inviteStepDismissed,
    markInviteSent,
    dismissInviteStep,
    setTreeChecklistCollapsed,
    setDashboardChecklistMinimized,
    setDashboardChecklistDismissed,
    isMvpCoreComplete,
    isOnboardingFullyDone,
  } = useOnboarding();

  const isDashboardSurface = surface === 'dashboard';

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [expandedManual, setExpandedManual] = useState({});

  const accountComplete = Boolean(user?.id);
  const step5Resolved = hasInviteSent || inviteStepDismissed;

  const ownerTree = trees.find((t) => t.role === 'owner');
  const inviteTreeId = ownerTree?.id;

  const completion = useMemo(
    () => ({
      1: accountComplete,
      2: hasCreatedFirstTree,
      3: hasAddedFirstPerson,
      4: hasRecordedFirstStory,
      5: step5Resolved,
    }),
    [
      accountComplete,
      hasCreatedFirstTree,
      hasAddedFirstPerson,
      hasRecordedFirstStory,
      step5Resolved,
    ]
  );

  const nextIncompleteId = useMemo(() => {
    for (const s of STEPS) {
      if (!completion[s.id]) return s.id;
    }
    return null;
  }, [completion]);

  const firstTreeId = trees[0]?.id;

  const isRowExpanded = (stepId, done) => {
    if (done) {
      return expandedManual[stepId] ?? false;
    }
    return nextIncompleteId === stepId;
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteTreeId || !inviteEmail.trim()) {
      toast.error('Enter an email address');
      return;
    }
    setInviteLoading(true);
    try {
      const inv = await axios.post(`${API}/trees/${inviteTreeId}/invite`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      const invitationId = inv.data?.id;
      if (invitationId) {
        try {
          const send = await axios.post(`${API}/invitations/${invitationId}/send-email`, {
            recipient_email: inviteEmail.trim(),
          });
          const msg =
            send.data?.message ||
            (send.status === 202 ? 'Invitation email queued — it should arrive shortly.' : 'Invitation sent.');
          toast.success(msg);
        } catch (sendErr) {
          toast.error(
            humanizeError(sendErr) ||
              'Invitation was created but the email could not be sent. Try again from Tree members, or share the invite link.',
          );
        }
      } else {
        toast.success('Invitation created');
      }
      markInviteSent();
      setInviteEmail('');
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setInviteLoading(false);
    }
  };

  if (isOnboardingFullyDone) {
    return null;
  }

  const toggleRow = (stepId, done) => {
    if (done) {
      setExpandedManual((prev) => ({
        ...prev,
        [stepId]: !(prev[stepId] ?? false),
      }));
    }
  };

  return (
    <div
      className={`overflow-hidden rounded-2xl border border-[hsl(38_18%_86%)] bg-gradient-to-b from-[hsl(42_28%_99%)] to-[hsl(40_22%_97.5%)] shadow-[0_4px_24px_hsl(28_12%_18%_/_0.06)] ${className}`}
      data-testid="onboarding-checklist"
    >
      <div className="border-b border-[hsl(38_18%_88%_/_0.95)] bg-[hsl(42_26%_98.5%_/_0.85)] px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1">
            <p className="font-serif text-lg tracking-tight text-[hsl(24_14%_16%)] sm:text-xl">
              Set up your family archive
            </p>
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-[hsl(32_12%_36%)]">
              {isMvpCoreComplete
                ? 'Optional last step: invite someone by email, or skip — your archive is already taking shape.'
                : 'Work through each step in order. Your next step stays open so you always know what comes first.'}
            </p>
          </div>
          {showMinimizeControl && !isDashboardSurface && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 rounded-full border-[hsl(38_16%_82%)] bg-[hsl(42_24%_99%)] text-[hsl(28_14%_28%)] hover:bg-[hsl(40_20%_96%)]"
              onClick={() => setTreeChecklistCollapsed(true)}
              data-testid="onboarding-checklist-minimize"
            >
              <ChevronUp className="mr-1.5 h-4 w-4" aria-hidden />
              Minimize
            </Button>
          )}
          {isDashboardSurface && (
            <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full border-[hsl(38_16%_82%)] bg-[hsl(42_24%_99%)] text-[hsl(28_14%_28%)] hover:bg-[hsl(40_20%_96%)]"
                onClick={() => setDashboardChecklistMinimized(true)}
                data-testid="dashboard-checklist-minimize"
              >
                <ChevronUp className="mr-1.5 h-4 w-4" aria-hidden />
                Minimize
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full text-[hsl(32_12%_40%)] hover:bg-[hsl(42_24%_97%)] hover:text-[hsl(24_14%_22%)]"
                onClick={() => setDashboardChecklistDismissed(true)}
                data-testid="dashboard-checklist-dismiss"
              >
                Hide for now
              </Button>
            </div>
          )}
        </div>
      </div>

      <ul className="divide-y divide-[hsl(38_18%_88%_/_0.85)]">
        {STEPS.map((step) => {
          const done = completion[step.id];
          const isNext = nextIncompleteId === step.id;
          const expanded = isRowExpanded(step.id, done);
          const Icon = step.icon;

          const headerClass = `w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors sm:px-6 sm:py-3.5 ${
            done ? 'cursor-pointer opacity-[0.82] hover:bg-[hsl(42_24%_98%)]' : ''
          }`;

          const rowHeader = (
            <>
              <span
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                  done ? 'bg-[#c9a961]/20 text-[#5c4a32]' : 'bg-[#ebe6dc] text-[#68645e]'
                } ${isNext && !done ? 'ring-2 ring-[#c9a961]/45' : ''}`}
              >
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </span>
              <span className="flex-1 min-w-0">
                <span
                  className={`block text-sm font-medium ${done ? 'text-[#7a7268] line-through decoration-[#c9a961]/35' : 'text-[#1c1917]'}`}
                >
                  {step.title}
                </span>
                {expanded && !done && step.id === 1 && (
                  <span className="text-xs text-muted-foreground block mt-1">
                    You&apos;re signed in and ready to go.
                  </span>
                )}
              </span>
              {done && (
                <ChevronDown
                  className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ${
                    expanded ? 'rotate-180' : ''
                  }`}
                />
              )}
            </>
          );

          return (
            <li key={step.id} className={isNext && !done ? 'bg-[hsl(42_28%_98%_/_0.65)]' : ''}>
              {done ? (
                <button
                  type="button"
                  onClick={() => toggleRow(step.id, done)}
                  className={headerClass}
                  aria-expanded={expanded}
                >
                  {rowHeader}
                </button>
              ) : (
                <div className={headerClass} aria-current={isNext ? 'step' : undefined}>
                  {rowHeader}
                </div>
              )}

              {expanded && !done && step.id === 2 && (
                <div className="space-y-2 px-5 pb-5 pl-14 sm:px-6 sm:pl-16">
                  <p className="text-xs text-muted-foreground">
                    Give your family tree a name — for example a surname or &quot;My ancestors&quot;.
                  </p>
                  {typeof onOpenCreateTree === 'function' ? (
                    <Button
                      type="button"
                      size="sm"
                      className="rounded-full bg-primary text-primary-foreground shadow-[0_4px_14px_-4px_hsl(var(--primary)/0.45)] hover:bg-[hsl(152_28%_24%)]"
                      onClick={() => onOpenCreateTree()}
                      data-testid="checklist-create-tree-btn"
                    >
                      Create family tree
                    </Button>
                  ) : (
                    <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground shadow-[0_4px_14px_-4px_hsl(var(--primary)/0.45)] hover:bg-[hsl(152_28%_24%)]" variant="default">
                      <Link to="/dashboard" data-testid="checklist-go-dashboard-tree">
                        Go to dashboard
                      </Link>
                    </Button>
                  )}
                </div>
              )}

              {expanded && !done && step.id === 3 && firstTreeId && (
                <div className="space-y-2 px-5 pb-5 pl-14 sm:px-6 sm:pl-16">
                  <p className="text-xs text-muted-foreground">
                    Add yourself or a family connection to your tree.
                  </p>
                  <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground shadow-[0_4px_14px_-4px_hsl(var(--primary)/0.45)] hover:bg-[hsl(152_28%_24%)]" variant="default">
                    <Link to={buildTreePeopleAddPersonUrl(trees[0] || firstTreeId)} data-testid="checklist-add-person-link">
                      Add connection
                    </Link>
                  </Button>
                </div>
              )}

              {expanded && !done && step.id === 4 && firstTreeId && (
                <div className="space-y-2 px-5 pb-5 pl-14 sm:px-6 sm:pl-16">
                  <p className="text-xs text-muted-foreground">
                    Open a person, then add a story in Legacy Stories — write or record.
                  </p>
                  <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground shadow-[0_4px_14px_-4px_hsl(var(--primary)/0.45)] hover:bg-[hsl(152_28%_24%)]" variant="default">
                    <Link
                      to={`${treeHref(trees[0] || { id: firstTreeId })}?tab=grid`}
                      data-testid="checklist-record-memory-link"
                    >
                      Go to family tree
                    </Link>
                  </Button>
                </div>
              )}

              {expanded && !done && step.id === 5 && inviteTreeId && (
                <div className="space-y-3 px-5 pb-5 pl-14 sm:px-6 sm:pl-16">
                  <p className="text-xs text-muted-foreground">
                    Send an email invite so someone can view or help edit this family tree.
                  </p>
                  <form onSubmit={handleSendInvite} className="space-y-2 max-w-sm">
                    <div className="space-y-1">
                      <Label htmlFor="onboarding-invite-email" className="text-xs">
                        Email
                      </Label>
                      <Input
                        id="onboarding-invite-email"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="relative@example.com"
                        autoComplete="email"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Role</Label>
                      <Select value={inviteRole} onValueChange={setInviteRole}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        type="submit"
                        size="sm"
                        className="rounded-full bg-primary text-primary-foreground shadow-[0_4px_14px_-4px_hsl(var(--primary)/0.45)] hover:bg-[hsl(152_28%_24%)]"
                        disabled={inviteLoading || !inviteEmail.trim()}
                        data-testid="checklist-send-invite-btn"
                      >
                        {inviteLoading ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                            Sending…
                          </>
                        ) : (
                          'Send invite'
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="rounded-full"
                        onClick={() => dismissInviteStep()}
                        data-testid="checklist-invite-skip"
                      >
                        Skip
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {expanded && !done && step.id === 5 && !inviteTreeId && trees.length > 0 && (
                <div className="space-y-3 px-5 pb-5 pl-14 sm:px-6 sm:pl-16">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    You&apos;re all set — nothing else is required. Only the person who created this tree can send email invites;
                    if that isn&apos;t you, you can skip this step and keep working on the tree.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => dismissInviteStep()}
                    data-testid="checklist-invite-skip-non-owner"
                  >
                    Skip
                  </Button>
                </div>
              )}

              {expanded && !done && step.id === 5 && trees.length === 0 && (
                <div className="space-y-2 px-5 pb-5 pl-14 sm:px-6 sm:pl-16">
                  <p className="text-xs text-muted-foreground">
                    Create a family tree first, then you can invite relatives.
                  </p>
                  <Button asChild size="sm" className="rounded-full bg-primary text-primary-foreground shadow-[0_4px_14px_-4px_hsl(var(--primary)/0.45)] hover:bg-[hsl(152_28%_24%)]" variant="default">
                    <Link to="/dashboard">Go to dashboard</Link>
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default OnboardingChecklist;
